import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/24/solid'

import { products } from './productData'

import handleError from '../../../utils/handleApiError'
import {
  GuaranteeType,
  GuaranteeStatusEnum,
} from '../../../admin/guarantees/adminGuarantees.domain'
import { Button } from '../../common/button/Button'
import { BACKEND_URL } from '../../../config'
import { headers, noContentTypeHeaders } from '../../../utils/apiHeaders'
import { useAppSelector } from '../../../utils/hooks'
import { HorizontalAdminMenu } from '../../../admin/menu/HorizontalAdminMenu'
import { MENU_ITEM } from '../../../admin/menu/constants'
import { RouteLink } from '../../common'
import '../../repair-request/RepairRequestPage.scss'
import { ErrorBox } from '../../common/errorBox/ErrorBox'
import handleResponse from '../../../utils/handleApiResponse'

export const GuaranteeEditItem = () => {
  const { register, handleSubmit, errors, setValue, watch } = useForm<GuaranteeType>()
  const { t } = useTranslation()
  const { guaranteeNumber } = useParams<{ guaranteeNumber: string }>()
  const [showErrorMessage, setShowErrorMessage] = useState<boolean | string>(false)
  const [updatedGuarantee, setUpdatedGuarantee] = useState<boolean>(false)
  const [showInvoiceUpload, setShowInvoiceUpload] = useState<boolean>(false)
  const [uploadedInvoice, setUploadedInvoice] = useState<File | null>(null)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)

  const guaranteesList = useAppSelector((state) => state?.adminGuaranteesList?.guaranteesList)
  const guarantee = guaranteesList?.find((item) => item.guaranteeNumber === guaranteeNumber)

  const isEdit = !!guaranteeNumber && !!guarantee
  const submitButtonLabel = 'Update Guarantee'

  const inputOtherCheckbox = useRef<HTMLInputElement>(null)
  const inputOtherText = watch('find.sonstiges')

  useEffect(() => {
    if (inputOtherCheckbox.current && !!inputOtherText) {
      inputOtherCheckbox.current.checked = true
    }
  }, [inputOtherText])

  useEffect(() => {
    if (guarantee && isEdit) {
      Object.keys(guarantee).forEach((key) => {
        const value = guarantee[key as keyof GuaranteeType]
        if (value !== undefined && value !== null) {
          setValue(key, value)
        }
      })

      if (guarantee.music) {
        Object.keys(guarantee.music).forEach((musicKey) => {
          setValue(`music.${musicKey}`, guarantee.music[musicKey as keyof typeof guarantee.music])
        })
      }

      if (guarantee.find) {
        Object.keys(guarantee.find).forEach((findKey) => {
          setValue(`find.${findKey}`, guarantee.find[findKey as keyof typeof guarantee.find])
        })
      }
    }
  }, [guarantee, isEdit, setValue])

  const validateInvoiceFile = (file: File): true | string => {
    const validTypes = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf']
    const maxSize = 20 * 1024 * 1024 // 20MB

    if (!validTypes.includes(file.type)) {
      return 'Allowed file types: JPG, PNG, PDF'
    }

    if (file.size > maxSize) {
      return 'Maximum file size is 20MB'
    }

    return true
  }

  const handleInvoiceUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceError(null)
    const files = event.target.files

    if (!files?.length) return

    const file = files[0]
    const validation = validateInvoiceFile(file)

    if (validation === true) {
      setUploadedInvoice(file)
    } else {
      setInvoiceError(validation)
      setUploadedInvoice(null)
    }
  }

  const removeInvoiceFile = () => {
    setUploadedInvoice(null)
    setInvoiceError(null)

    const fileInput = document.getElementById('invoiceUpload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleFormSubmit = async (data: GuaranteeType) => {
    setShowErrorMessage(false)

    const completeData = {
      ...data,
      warrantyYears: Number(data.warrantyYears) || 0,
      stars: Number(data.stars) || 0,
      country: data.country || null,
      shopName: data.shopName || '',
      city: data.city || '',
    }

    try {
      // First, update the guarantee data
      const editURL = `${BACKEND_URL}/api/guarantee/${guaranteeNumber}/edit`
      const editRequestOptions = {
        method: 'PATCH',
        headers: headers('application/json'),
        body: JSON.stringify(completeData),
      }

      await fetch(editURL, editRequestOptions).then(handleResponse)

      // If there's an invoice to upload, make a separate call
      if (uploadedInvoice) {
        const invoiceURL = `${BACKEND_URL}/api/guarantee/${guaranteeNumber}/editInvoice`
        const formData = new FormData()
        formData.append('attachment', uploadedInvoice)

        const invoiceRequestOptions = {
          method: 'POST',
          headers: noContentTypeHeaders(),
          body: formData,
        }

        const invoiceResponse = await fetch(invoiceURL, invoiceRequestOptions)
        if (!invoiceResponse.ok) {
          throw invoiceResponse
        }
      }

      setUpdatedGuarantee(true)
    } catch (error) {
      setUpdatedGuarantee(false)
      setShowErrorMessage(true)
      return handleError(error as Response)
    }
  }

  if (!guarantee) {
    return <div className="text-center py-4">Guarantee not found</div>
  }

  return (
    <div className="flex justify-center">
      <div className="w-3/4 px-4 bg-stone-100 border-l border-r border-stone-300">
        <div>
          <HorizontalAdminMenu current={MENU_ITEM.USERS} />
          <div className="container">
            <div className="col-12">
              <div className="row mb-5">
                <Link to={RouteLink.ADMIN_USERS}>
                  <Button>
                    <div className="flex">
                      <div className="mr-2 -ml-2">
                        <ChevronLeftIcon className="h-4 w-4" />
                      </div>
                      <div className="font-normal tracking-wider text-gray-15">
                        {t('common.back')}
                      </div>
                    </div>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <form
          className="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          encType="multipart/form-data"
        >
          <div className="flex my-10">
            <div className="w-1/3 mr-4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="guaranteeNumber">
                  Guarantee Number *
                </label>
                <input
                  className="p-3 bg-gray-100"
                  ref={register({ required: true })}
                  type="text"
                  name="guaranteeNumber"
                  id="guaranteeNumber"
                  readOnly
                />
                {errors.guaranteeNumber && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeForm.requiredField')}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/3 mr-4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="serialNumber">
                  Serial Number *
                </label>
                <input
                  className="p-3"
                  ref={register({ required: true })}
                  type="text"
                  name="serialNumber"
                  id="serialNumber"
                />
                {errors.serialNumber && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeForm.requiredField')}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/3">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="guaranteeStatus">
                  Status *
                </label>
                <select
                  className="p-3"
                  ref={register({ required: true })}
                  name="guaranteeStatus"
                  id="guaranteeStatus"
                >
                  <option value={GuaranteeStatusEnum.RECEIVED}>
                    {GuaranteeStatusEnum.RECEIVED}
                  </option>
                  <option value={GuaranteeStatusEnum.CONFIRMED}>
                    {GuaranteeStatusEnum.CONFIRMED}
                  </option>
                </select>
                {errors.guaranteeStatus && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeForm.requiredField')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex mb-10">
            <div className="w-1/2 mr-4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="component">
                  {'Choose product'}
                </label>
                <select
                  className="p-3"
                  ref={register({ required: true })}
                  name="component"
                  id="component"
                >
                  {products.map((item) => (
                    <option key={item} value={item}>
                      {t(`products.guaranteeEdit.${item}`)}
                    </option>
                  ))}
                </select>
                {errors.component && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeForm.requiredField')}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/4 mr-4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="warrantyYears">
                  Warranty Years *
                </label>
                <input
                  className="p-3"
                  ref={register({ required: true, min: 1 })}
                  type="number"
                  name="warrantyYears"
                  id="warrantyYears"
                />
                {errors.warrantyYears && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeForm.requiredField')}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="stars">
                  Stars (1-5) *
                </label>
                <input
                  className="p-3"
                  ref={register({ required: true, min: 1, max: 5 })}
                  type="number"
                  name="stars"
                  id="stars"
                  min="1"
                  max="5"
                />
                {errors.stars && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeForm.requiredField')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex mb-10">
            <div className="w-1/2 mr-4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="customerName">
                  Customer Name *
                </label>
                <input
                  className="p-3"
                  ref={register({ required: true })}
                  type="text"
                  name="customerName"
                  id="customerName"
                />
                {errors.customerName && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeEdit.requiredField')}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/2">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="customerEmail">
                  Customer Email *
                </label>
                <input
                  className="p-3"
                  ref={register({ required: true })}
                  type="email"
                  name="customerEmail"
                  id="customerEmail"
                />
                {errors.customerEmail && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeEdit.requiredField')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex mb-10">
            <div className="w-1/2 mr-4">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="purchaseDate">
                  {`${t('repairRequest.form.purchaseDate')} *`}
                </label>
                <input
                  ref={register({ required: true, valueAsDate: true })}
                  type="date"
                  name="purchaseDate"
                  className="p-3"
                />
                {errors.purchaseDate?.type === 'required' && (
                  <div className="text-red-500 font-bold mt-2">
                    {t('guaranteeEdit.requiredField')}
                  </div>
                )}

                <div className="mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={showInvoiceUpload}
                      onChange={(e) => {
                        setShowInvoiceUpload(e.target.checked)
                        if (!e.target.checked) {
                          setUploadedInvoice(null)
                          setInvoiceError(null)
                          const fileInput = document.getElementById(
                            'invoiceUpload'
                          ) as HTMLInputElement
                          if (fileInput) {
                            fileInput.value = ''
                          }
                        }
                      }}
                    />
                    <span className="font-medium">Upload purchase document</span>
                  </label>
                </div>

                {showInvoiceUpload && (
                  <div className="mt-4 p-4 border border-gray-300 rounded">
                    <label className="font-bold block mb-2" htmlFor="invoiceUpload">
                      Purchase Document
                    </label>

                    {!uploadedInvoice ? (
                      <div>
                        <input
                          type="file"
                          id="invoiceUpload"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={handleInvoiceUpload}
                          className="mb-2"
                        />
                        <div className="text-sm text-gray-600">
                          Allowed file types: JPG, PNG, PDF (max 20MB)
                        </div>
                        {invoiceError && (
                          <div className="text-red-500 font-bold mt-2 text-sm">{invoiceError}</div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-gray-100 rounded">
                        <div className="flex items-center">
                          <svg
                            className="w-5 h-5 mr-2 text-gray-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">{uploadedInvoice.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeInvoiceFile}
                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="w-1/2"></div>
          </div>

          <div className="mb-10">
            <h3 className="font-bold text-lg mb-4">{t('guaranteeEdit.musicSection')}</h3>
            <div className="flex flex-wrap">
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="music.cd" className="mr-2" />
                  <span>CD</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="music.vinyl" className="mr-2" />
                  <span>Vinyl</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="music.analog" className="mr-2" />
                  <span>Analog</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="music.digital" className="mr-2" />
                  <span>Digital</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="music.streaming" className="mr-2" />
                  <span>Streaming</span>
                </label>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h3 className="font-bold text-lg mb-4">{t('guaranteeEdit.findSection')}</h3>
            <div className="flex flex-wrap">
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="find.anzeige" className="mr-2" />
                  <span>Advertisement</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="find.artikel" className="mr-2" />
                  <span>Article</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input ref={register} type="checkbox" name="find.internet" className="mr-2" />
                  <span>Internet</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input
                    ref={register}
                    type="checkbox"
                    name="find.autorisierter"
                    className="mr-2"
                  />
                  <span>Authorized Dealer</span>
                </label>
              </div>
              <div className="w-1/5 mb-4">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" ref={inputOtherCheckbox} />
                  <span>Other:</span>
                  <input className="input-other" ref={register} name="find.sonstiges" type="text" />
                </label>
              </div>
            </div>
          </div>

          <div className="mb-5">
            <h3 className="font-bold text-lg mb-4">Acceptance Regulations</h3>
            <div className="flex flex-wrap">
              <div className="w-11/12 mr-4 mb-4">
                <label className="flex items-center">
                  <input
                    ref={register({ required: true })}
                    type="checkbox"
                    name="acceptanceRegulationsCheckboxOne"
                    className="mr-2"
                  />
                  <span>{t('guaranteeEdit.dataConsent')}</span>
                </label>
              </div>
              <div className="w-11/12 mb-4">
                <label className="flex items-center">
                  <input
                    ref={register}
                    type="checkbox"
                    name="acceptanceRegulationsCheckboxTwo"
                    className="mr-2"
                  />
                  <p>{t('guaranteeEdit.dataConsentTwo')}</p>
                </label>
                <div>
                  <p style={{ marginLeft: '30px' }}>
                    {t('guaranteeEdit.dataConsentTwoOne')}
                    <br />
                    {t('guaranteeEdit.dataConsentTwoTwo')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex mb-10">
            <div className="w-1/2">
              <div className="flex flex-col">
                <label className="font-bold" htmlFor="language">
                  Language *
                </label>
                <select
                  className="p-3"
                  ref={register({ required: true })}
                  name="language"
                  id="language"
                >
                  <option value="">Select Language</option>
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                  <option value="ch">Chinese</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex mb-10">
            <div className="w-1/2"></div>
            <div className="w-1/2">
              <div className="flex justify-end">
                <Button type="submit">
                  <div className="flex">
                    <div className="font-normal tracking-wider text-gray-15">
                      {submitButtonLabel}
                    </div>
                  </div>
                </Button>
              </div>
            </div>
          </div>
          {updatedGuarantee ? (
            <div className="text-xl font-bold" style={{ color: 'darkgreen' }}>
              Guarantee was updated
            </div>
          ) : (
            <></>
          )}
        </form>
        {showErrorMessage ? (
          <ErrorBox
            title={t('repairRequest.error')}
            text={` ${t('repairRequest.errorMessage')}`}
            onClick={() => setShowErrorMessage(false)}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  )
}
